import EventEmitter from 'events';

import { z } from 'zod';

import {
  tokenUsageSchema,
  toolUsageSchema,
  globalSettingsSchema,
  mcpMarketplaceItemSchema,
  discriminatedProviderSettingsWithIdSchema,
} from '@roo-code/types';

/**
 * CloudUserInfo
 */

export interface CloudUserInfo {
  id?: string;
  name?: string;
  email?: string;
  picture?: string;
  organizationId?: string;
  organizationName?: string;
  organizationRole?: string;
  organizationImageUrl?: string;
  extensionBridgeEnabled?: boolean;
}

/**
 * CloudOrganization
 */

export interface CloudOrganization {
  id: string;
  name: string;
  slug?: string;
  image_url?: string;
  has_image?: boolean;
  created_at?: number;
  updated_at?: number;
}

/**
 * CloudOrganizationMembership
 */

export interface CloudOrganizationMembership {
  id: string;
  organization: CloudOrganization;
  role: string;
  permissions?: string[];
  created_at?: number;
  updated_at?: number;
}

/**
 * OrganizationAllowList
 */

export const organizationAllowListSchema = z.object({
  allowAll: z.boolean(),
  providers: z.record(
    z.object({
      allowAll: z.boolean(),
      models: z.array(z.string()).optional(),
    }),
  ),
});

export type OrganizationAllowList = z.infer<typeof organizationAllowListSchema>;

/**
 * OrganizationDefaultSettings
 */

export const organizationDefaultSettingsSchema = globalSettingsSchema
  .pick({
    enableCheckpoints: true,
    fuzzyMatchThreshold: true,
    maxOpenTabsContext: true,
    maxReadFileLine: true,
    maxWorkspaceFiles: true,
    showRooIgnoredFiles: true,
    terminalCommandDelay: true,
    terminalCompressProgressBar: true,
    terminalOutputLineLimit: true,
    terminalShellIntegrationDisabled: true,
    terminalShellIntegrationTimeout: true,
    terminalZshClearEolMark: true,
  })
  // Add stronger validations for some fields.
  .merge(
    z.object({
      maxOpenTabsContext: z.number().int().nonnegative().optional(),
      maxReadFileLine: z.number().int().gte(-1).optional(),
      maxWorkspaceFiles: z.number().int().nonnegative().optional(),
      terminalCommandDelay: z.number().int().nonnegative().optional(),
      terminalOutputLineLimit: z.number().int().nonnegative().optional(),
      terminalShellIntegrationTimeout: z
        .number()
        .int()
        .nonnegative()
        .optional(),
    }),
  );

export type OrganizationDefaultSettings = z.infer<
  typeof organizationDefaultSettingsSchema
>;

/**
 * OrganizationCloudSettings
 */

export const organizationCloudSettingsSchema = z.object({
  recordTaskMessages: z.boolean().optional(),
  enableTaskSharing: z.boolean().optional(),
  taskShareExpirationDays: z.number().int().positive().optional(),
  allowMembersViewAllTasks: z.boolean().optional(),
});

export type OrganizationCloudSettings = z.infer<
  typeof organizationCloudSettingsSchema
>;

/**
 * OrganizationSettings
 */

export const organizationSettingsSchema = z.object({
  version: z.number(),
  cloudSettings: organizationCloudSettingsSchema.optional(),
  defaultSettings: organizationDefaultSettingsSchema,
  allowList: organizationAllowListSchema,
  hiddenMcps: z.array(z.string()).optional(),
  hideMarketplaceMcps: z.boolean().optional(),
  mcps: z.array(mcpMarketplaceItemSchema).optional(),
  providerProfiles: z
    .record(z.string(), discriminatedProviderSettingsWithIdSchema)
    .optional(),
});

export type OrganizationSettings = z.infer<typeof organizationSettingsSchema>;

/**
 * Constants
 */

export const ORGANIZATION_ALLOW_ALL: OrganizationAllowList = {
  allowAll: true,
  providers: {},
} as const;

export const ORGANIZATION_DEFAULT: OrganizationSettings = {
  version: 0,
  cloudSettings: {
    recordTaskMessages: true,
    enableTaskSharing: true,
    taskShareExpirationDays: 30,
    allowMembersViewAllTasks: true,
  },
  defaultSettings: {},
  allowList: ORGANIZATION_ALLOW_ALL,
} as const;

/**
 * ShareVisibility
 */

export type ShareVisibility = 'organization' | 'public';

/**
 * ShareResponse
 */

export const shareResponseSchema = z.object({
  success: z.boolean(),
  shareUrl: z.string().optional(),
  error: z.string().optional(),
  isNewShare: z.boolean().optional(),
  manageUrl: z.string().optional(),
});

export type ShareResponse = z.infer<typeof shareResponseSchema>;

/**
 * AuthService
 */

export type AuthState =
  | 'initializing'
  | 'logged-out'
  | 'active-session'
  | 'attempting-session'
  | 'inactive-session';

export interface AuthService extends EventEmitter<AuthServiceEvents> {
  // Lifecycle
  initialize(): Promise<void>;

  // Authentication methods
  login(): Promise<void>;
  logout(): Promise<void>;
  handleCallback(
    code: string | null,
    state: string | null,
    organizationId?: string | null,
  ): Promise<void>;

  // State methods
  getState(): AuthState;
  isAuthenticated(): boolean;
  hasActiveSession(): boolean;
  hasOrIsAcquiringActiveSession(): boolean;

  // Token and user info
  getSessionToken(): string | undefined;
  getUserInfo(): CloudUserInfo | null;
  getStoredOrganizationId(): string | null;
}

/**
 * AuthServiceEvents
 */

export interface AuthServiceEvents {
  'auth-state-changed': [
    data: {
      state: AuthState;
      previousState: AuthState;
    },
  ];
  'user-info': [data: { userInfo: CloudUserInfo }];
}

/**
 * SettingsService
 */

/**
 * Interface for settings services that provide organization settings
 */
export interface SettingsService {
  /**
   * Get the organization allow list
   * @returns The organization allow list or default if none available
   */
  getAllowList(): OrganizationAllowList;

  /**
   * Get the current organization settings
   * @returns The organization settings or undefined if none available
   */
  getSettings(): OrganizationSettings | undefined;

  /**
   * Dispose of the settings service and clean up resources
   */
  dispose(): void;
}

/**
 * SettingsServiceEvents
 */

export interface SettingsServiceEvents {
  'settings-updated': [
    data: {
      settings: OrganizationSettings;
      previousSettings: OrganizationSettings | undefined;
    },
  ];
}

/**
 * CloudServiceEvents
 */

export type CloudServiceEvents = AuthServiceEvents & SettingsServiceEvents;

/**
 * ConnectionState
 */

export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RETRYING = 'retrying',
  FAILED = 'failed',
}

/**
 * RetryConfig
 */

export interface RetryConfig {
  maxInitialAttempts: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

/**
 * Constants
 */

export const HEARTBEAT_INTERVAL_MS = 20_000;
export const INSTANCE_TTL_SECONDS = 60;

/**
 * BridgeNamespace
 */

export enum BridgeNamespace {
  Extension = 'bridge:extension',
  Task = 'bridge:task',
}

/**
 * ExtensionInstance
 */

export enum ExtensionInstanceField {
  InstanceId = 'instanceId',
  UserId = 'userId',
  WorkspacePath = 'workspacePath',
  ExtensionVersion = 'extensionVersion',
  LastHeartbeat = 'lastHeartbeat',
  TaskId = 'taskId',
  TaskStatus = 'taskStatus',
}

export enum TaskStatus {
  Running = 'running',
  Idle = 'idle',
  None = 'none',
}

export const extensionInstanceSchema = z.object({
  [ExtensionInstanceField.InstanceId]: z.string(),
  [ExtensionInstanceField.UserId]: z.string(),
  [ExtensionInstanceField.WorkspacePath]: z.string(),
  [ExtensionInstanceField.ExtensionVersion]: z.string(),
  [ExtensionInstanceField.LastHeartbeat]: z.coerce.number(),
  [ExtensionInstanceField.TaskId]: z.string(),
  [ExtensionInstanceField.TaskStatus]: z.nativeEnum(TaskStatus),
});

export type ExtensionInstance = z.infer<typeof extensionInstanceSchema>;

/**
 * ExtensionBridgeChannel
 */

export enum ExtensionBridgeChannel {
  Control = 'control',
  Events = 'events',
  Instances = 'instances',
  Instance = 'instance',
}

/**
 * ExtensionBridgeEventChannel
 */

export enum ExtensionBridgeEventChannelMessageType {
  TaskCreated = 'task_created',
  TaskStarted = 'task_started',
  TaskCompleted = 'task_completed',
  TaskAborted = 'task_aborted',
  TaskFocused = 'task_focused',
  TaskUnfocused = 'task_unfocused',
  TaskActive = 'task_active',
  TaskIdle = 'task_idle',
  InstanceRegistered = 'instance_registered',
  InstanceUnregistered = 'instance_unregistered',
  HeartbeatUpdated = 'heartbeat_updated',
}

export const extensionBridgeEventChannelMessageSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskCreated),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskStarted),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskCompleted),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
        tokenUsage: tokenUsageSchema,
        toolUsage: toolUsageSchema,
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskAborted),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskFocused),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskUnfocused),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskActive),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.TaskIdle),
      instanceId: z.string(),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(
        ExtensionBridgeEventChannelMessageType.InstanceRegistered,
      ),
      instanceId: z.string(),
      payload: z.object({ instance: extensionInstanceSchema }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(
        ExtensionBridgeEventChannelMessageType.InstanceUnregistered,
      ),
      instanceId: z.string(),
      payload: z.object({ instanceId: z.string() }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeEventChannelMessageType.HeartbeatUpdated),
      instanceId: z.string(),
      payload: z.object({ instance: extensionInstanceSchema }),
      timestamp: z.number(),
    }),
  ],
);

export type ExtensionBridgeEventChannelMessage = z.infer<
  typeof extensionBridgeEventChannelMessageSchema
>;

/**
 * ExtensionBridgeControlChannel
 */

export enum ExtensionBridgeControlChannelMessageType {
  StartTask = 'start_task',
  StopTask = 'stop_task',
}

export const extensionBridgeControlChannelMessageSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      type: z.literal(ExtensionBridgeControlChannelMessageType.StartTask),
      instanceId: z.string(),
      payload: z.object({
        text: z.string(),
        images: z.array(z.string()).optional(),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(ExtensionBridgeControlChannelMessageType.StopTask),
      instanceId: z.string(),
      payload: z.object({ taskId: z.string() }),
      timestamp: z.number(),
    }),
  ],
);

export type ExtensionBridgeControlChannelMessage = z.infer<
  typeof extensionBridgeControlChannelMessageSchema
>;

/**
 * TaskBridgeChannel
 */

export enum TaskBridgeChannel {
  Control = 'control',
  Events = 'events',
}

/**
 * TaskBridgeEventChannelMessage
 */

export enum TaskBridgeEventChannelMessageType {
  TaskStarted = 'task_started',
  TaskCompleted = 'task_completed',
  TaskAborted = 'task_aborted',
  TaskFocused = 'task_focused',
  TaskUnfocused = 'task_unfocused',
  TaskActive = 'task_active',
  TaskIdle = 'task_idle',
}

export const taskBridgeEventChannelMessageSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      type: z.literal(TaskBridgeEventChannelMessageType.TaskStarted),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(TaskBridgeEventChannelMessageType.TaskCompleted),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
        tokenUsage: tokenUsageSchema,
        toolUsage: toolUsageSchema,
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(TaskBridgeEventChannelMessageType.TaskAborted),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(TaskBridgeEventChannelMessageType.TaskFocused),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(TaskBridgeEventChannelMessageType.TaskUnfocused),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(TaskBridgeEventChannelMessageType.TaskActive),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
    z.object({
      type: z.literal(TaskBridgeEventChannelMessageType.TaskIdle),
      payload: z.object({
        taskId: z.string(),
        taskStatus: z.nativeEnum(TaskStatus),
      }),
      timestamp: z.number(),
    }),
  ],
);

export type TaskBridgeEventChannelMessage = z.infer<
  typeof taskBridgeEventChannelMessageSchema
>;

/**
 * TaskBridgeControlChannelMessage
 */

export enum TaskBridgeControlChannelMessageType {
  Message = 'message',
}

export const taskBridgeControlChannelMessageSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      type: z.literal(TaskBridgeControlChannelMessageType.Message),
      taskId: z.string(),
      payload: z.object({
        text: z.string(),
        images: z.array(z.string()).optional(),
      }),
      timestamp: z.number(),
    }),
  ],
);

export type TaskBridgeControlChannelMessage = z.infer<
  typeof taskBridgeControlChannelMessageSchema
>;
